/**
 * Deploy AI - Code Examples
 * 
 * Contains all demo infrastructure code examples
 * and the logic to select appropriate code based on user input
 */

export const CODE_EXAMPLES = {
  docker: `# Docker - Containerized Application
FROM node:18-alpine

WORKDIR /app

# Copy package files
COPY package*.json ./

# Install dependencies
RUN npm ci --only=production

# Copy application code
COPY . .

# Expose port
EXPOSE 3000

# Health check
HEALTHCHECK --interval=30s --timeout=3s \\
  CMD node healthcheck.js

# Start application
CMD ["node", "server.js"]`,

  kubernetes: `# Kubernetes - Deployment Configuration
apiVersion: apps/v1
kind: Deployment
metadata:
  name: web-app
  labels:
    app: web-app
spec:
  replicas: 3
  selector:
    matchLabels:
      app: web-app
  template:
    metadata:
      labels:
        app: web-app
    spec:
      containers:
      - name: web-app
        image: myapp:latest
        ports:
        - containerPort: 3000
        resources:
          limits:
            memory: "512Mi"
            cpu: "500m"
---
apiVersion: v1
kind: Service
metadata:
  name: web-app-service
spec:
  selector:
    app: web-app
  ports:
  - port: 80
    targetPort: 3000
  type: LoadBalancer`,

  ansible: `# Ansible - Server Configuration
---
- name: Configure Web Server
  hosts: webservers
  become: yes
  
  tasks:
    - name: Update apt cache
      apt:
        update_cache: yes
        
    - name: Install Nginx
      apt:
        name: nginx
        state: present
        
    - name: Install Node.js
      apt:
        name: nodejs
        state: present
        
    - name: Start Nginx service
      service:
        name: nginx
        state: started
        enabled: yes
        
    - name: Copy application files
      copy:
        src: /local/app/
        dest: /var/www/html/
        
    - name: Configure firewall
      ufw:
        rule: allow
        port: '80'
        proto: tcp`,

  database: `# Terraform - Database Infrastructure
provider "aws" {
  region = "us-east-1"
}

resource "aws_db_instance" "postgres" {
  identifier           = "myapp-db"
  engine              = "postgres"
  engine_version      = "14.7"
  instance_class      = "db.t3.micro"
  allocated_storage   = 20
  storage_encrypted   = true
  
  db_name  = "myappdb"
  username = "dbadmin"
  password = var.db_password
  
  vpc_security_group_ids = [aws_security_group.db.id]
  db_subnet_group_name   = aws_db_subnet_group.main.name
  
  backup_retention_period = 7
  skip_final_snapshot    = false
  
  tags = {
    Name = "MyApp Database"
  }
}

resource "aws_security_group" "db" {
  name = "database-sg"
  
  ingress {
    from_port   = 5432
    to_port     = 5432
    protocol    = "tcp"
    cidr_blocks = ["10.0.0.0/16"]
  }
}`,

  redis: `# Docker Compose - Redis Cache
version: '3.8'

services:
  redis:
    image: redis:7-alpine
    container_name: redis-cache
    ports:
      - "6379:6379"
    volumes:
      - redis-data:/data
    command: redis-server --appendonly yes
    restart: unless-stopped
    healthcheck:
      test: ["CMD", "redis-cli", "ping"]
      interval: 10s
      timeout: 3s
      retries: 3
      
  redis-commander:
    image: rediscommander/redis-commander:latest
    container_name: redis-ui
    environment:
      - REDIS_HOSTS=local:redis:6379
    ports:
      - "8081:8081"
    depends_on:
      - redis
      
volumes:
  redis-data:`,

  microservices: `# Kubernetes - Microservices Architecture
apiVersion: v1
kind: Namespace
metadata:
  name: microservices
---
apiVersion: apps/v1
kind: Deployment
metadata:
  name: api-gateway
  namespace: microservices
spec:
  replicas: 2
  selector:
    matchLabels:
      app: api-gateway
  template:
    metadata:
      labels:
        app: api-gateway
    spec:
      containers:
      - name: gateway
        image: api-gateway:latest
        ports:
        - containerPort: 8080
---
apiVersion: apps/v1
kind: Deployment
metadata:
  name: user-service
  namespace: microservices
spec:
  replicas: 3
  selector:
    matchLabels:
      app: user-service
  template:
    metadata:
      labels:
        app: user-service
    spec:
      containers:
      - name: user-service
        image: user-service:latest
        ports:
        - containerPort: 3001
---
apiVersion: apps/v1
kind: Deployment
metadata:
  name: order-service
  namespace: microservices
spec:
  replicas: 3
  selector:
    matchLabels:
      app: order-service
  template:
    metadata:
      labels:
        app: order-service
    spec:
      containers:
      - name: order-service
        image: order-service:latest
        ports:
        - containerPort: 3002`,

  loadbalancer: `# Terraform - Load Balancer Configuration
provider "aws" {
  region = "us-east-1"
}

resource "aws_lb" "main" {
  name               = "app-load-balancer"
  internal           = false
  load_balancer_type = "application"
  security_groups    = [aws_security_group.lb.id]
  subnets           = aws_subnet.public[*].id
  
  enable_deletion_protection = false
  
  tags = {
    Name = "App Load Balancer"
  }
}

resource "aws_lb_target_group" "app" {
  name     = "app-target-group"
  port     = 80
  protocol = "HTTP"
  vpc_id   = aws_vpc.main.id
  
  health_check {
    enabled             = true
    healthy_threshold   = 2
    interval            = 30
    matcher            = "200"
    path               = "/health"
    port               = "traffic-port"
    protocol           = "HTTP"
    timeout            = 5
    unhealthy_threshold = 2
  }
}

resource "aws_lb_listener" "front_end" {
  load_balancer_arn = aws_lb.main.arn
  port              = "80"
  protocol          = "HTTP"
  
  default_action {
    type             = "forward"
    target_group_arn = aws_lb_target_group.app.arn
  }
}`,

  cicd: `# GitHub Actions - CI/CD Pipeline
name: Deploy Application

on:
  push:
    branches: [ main ]
  pull_request:
    branches: [ main ]

jobs:
  test:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v3
      
      - name: Setup Node.js
        uses: actions/setup-node@v3
        with:
          node-version: '18'
          
      - name: Install dependencies
        run: npm ci
        
      - name: Run tests
        run: npm test
        
      - name: Run linter
        run: npm run lint

  build:
    needs: test
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v3
      
      - name: Build Docker image
        run: docker build -t myapp:latest .
        
      - name: Push to registry
        run: |
          echo \${{ secrets.DOCKER_PASSWORD }} | docker login -u \${{ secrets.DOCKER_USERNAME }} --password-stdin
          docker push myapp:latest

  deploy:
    needs: build
    runs-on: ubuntu-latest
    steps:
      - name: Deploy to production
        run: |
          kubectl set image deployment/myapp myapp=myapp:latest
          kubectl rollout status deployment/myapp`,

  serverless: `# Terraform - Serverless Lambda Function
provider "aws" {
  region = "us-east-1"
}

resource "aws_lambda_function" "api" {
  filename      = "lambda_function.zip"
  function_name = "api-handler"
  role          = aws_iam_role.lambda.arn
  handler       = "index.handler"
  runtime       = "nodejs18.x"
  
  environment {
    variables = {
      ENVIRONMENT = "production"
    }
  }
  
  timeout = 30
  memory_size = 512
}

resource "aws_api_gateway_rest_api" "api" {
  name        = "serverless-api"
  description = "Serverless API Gateway"
}

resource "aws_api_gateway_resource" "proxy" {
  rest_api_id = aws_api_gateway_rest_api.api.id
  parent_id   = aws_api_gateway_rest_api.api.root_resource_id
  path_part   = "{proxy+}"
}

resource "aws_api_gateway_method" "proxy" {
  rest_api_id   = aws_api_gateway_rest_api.api.id
  resource_id   = aws_api_gateway_resource.proxy.id
  http_method   = "ANY"
  authorization = "NONE"
}

resource "aws_lambda_permission" "api_gateway" {
  statement_id  = "AllowAPIGatewayInvoke"
  action        = "lambda:InvokeFunction"
  function_name = aws_lambda_function.api.function_name
  principal     = "apigateway.amazonaws.com"
}`,

  default: `# Terraform - AWS Infrastructure
provider "aws" {
  region = "us-east-1"
}

resource "aws_instance" "web" {
  ami           = "ami-0c55b159cbfafe1f0"
  instance_type = "t2.micro"
  
  tags = {
    Name = "web-server"
  }
}

resource "aws_security_group" "web" {
  name = "web-sg"
  
  ingress {
    from_port   = 80
    to_port     = 80
    protocol    = "tcp"
    cidr_blocks = ["0.0.0.0/0"]
  }
  
  egress {
    from_port   = 0
    to_port     = 0
    protocol    = "-1"
    cidr_blocks = ["0.0.0.0/0"]
  }
}`
};

/**
 * Get appropriate code example based on user's description
 * @param {string} description - User's infrastructure description
 * @returns {string} - Matching code example
 */
export const getCodeExample = (description) => {
  const lowerDesc = description.toLowerCase();
  
  if (lowerDesc.includes('docker') || lowerDesc.includes('container')) {
    return CODE_EXAMPLES.docker;
  } else if (lowerDesc.includes('kubernetes') || lowerDesc.includes('k8s') || lowerDesc.includes('cluster')) {
    return CODE_EXAMPLES.kubernetes;
  } else if (lowerDesc.includes('ansible') || lowerDesc.includes('configuration')) {
    return CODE_EXAMPLES.ansible;
  } else if (lowerDesc.includes('database') || lowerDesc.includes('postgres') || lowerDesc.includes('mysql') || lowerDesc.includes('sql')) {
    return CODE_EXAMPLES.database;
  } else if (lowerDesc.includes('redis') || lowerDesc.includes('cache')) {
    return CODE_EXAMPLES.redis;
  } else if (lowerDesc.includes('microservice') || lowerDesc.includes('micro service')) {
    return CODE_EXAMPLES.microservices;
  } else if (lowerDesc.includes('load balancer') || lowerDesc.includes('loadbalancer') || lowerDesc.includes('lb')) {
    return CODE_EXAMPLES.loadbalancer;
  } else if (lowerDesc.includes('ci/cd') || lowerDesc.includes('pipeline') || lowerDesc.includes('github actions')) {
    return CODE_EXAMPLES.cicd;
  } else if (lowerDesc.includes('serverless') || lowerDesc.includes('lambda') || lowerDesc.includes('function')) {
    return CODE_EXAMPLES.serverless;
  } else {
    return CODE_EXAMPLES.default;
  }
};

// Made with Bob
